import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import Swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';
import { LlaveService } from 'src/app/shared/services/llave.service';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { AlertService } from 'src/app/shared/services/alert.service';
import { PlatformMessagesService } from 'src/app/dashboard/services/platform-messages.service';
import { UserRoles } from 'src/app/shared/enums/roles-enum';

/** Marca de que el panel ya se ofreció en esta sesión del navegador. */
const YA_OFRECIDO = 'aguacate_llave_ofrecida';

/** Cuántas novedades se listan antes de resumir el resto en "y N más". */
const NOVEDADES_VISIBLES = 3;

interface Novedad {
  asunto: string;
  cuando: string;
}

interface Pendientes {
  items: Novedad[];
  total: number;
}

@Component({
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.sass'],
})
export class HomeComponent implements OnInit {
  constructor(
    private llaveService: LlaveService,
    private cognito: CognitoService,
    private mensajes: PlatformMessagesService,
    private alerts: AlertService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.panelDeBienvenida();
  }

  /**
   * UN solo panel al entrar, con todo lo que el usuario tiene pendiente.
   *
   * Antes esto eran dos modales sueltos peleando por la misma pantalla: el
   * del usuario Aguacate, que vive acá, y el de mensajes sin leer, que
   * disparaba el navbar por su cuenta. Cuál ganaba dependía de qué llamada
   * volvía primero, y el que perdía se borraba sin que nadie lo viera.
   *
   * Ahora hay uno con dos secciones —lo que hay que hacer y lo que hay que
   * saber— y un camino por cada cosa. Las reglas:
   *
   *  - Sólo al entrar y una vez por sesión del navegador. Nunca en otra
   *    pantalla: un panel a mitad de una transferencia es una interrupción.
   *  - Sólo si hay algo pendiente de verdad. Sin usuario que crear y sin
   *    mensajes sin leer, no se abre nada.
   *  - La sección del usuario Aguacate es sólo para clientes: un operador no
   *    tiene fila en `user` y nunca va a tener uno. Los mensajes son de los
   *    dos, cada uno a su bandeja.
   *  - Va como `oferta` ante el portero: no interrumpe nada. Si hay un modal
   *    abierto se descarta y se vuelve a ofrecer en la próxima visita.
   */
  private async panelDeBienvenida(): Promise<void> {
    // Durante el registro el usuario todavía está en el wizard: el dashboard
    // no es suyo aún y el panel se le cruzaría encima.
    if (sessionStorage.getItem('aguacate_registration_in_progress') === 'true') return;
    if (sessionStorage.getItem(YA_OFRECIDO) === 'true') return;

    const grupos = await this.gruposDeCognito();
    const esCliente =
      grupos.includes(UserRoles.aguacate_client) ||
      grupos.includes(UserRoles.aguacate_retail_client);
    const esOperador = grupos.includes(UserRoles.aguacate_operator);

    // Sin poder determinar el rol no se ofrece nada: se verá la próxima vez.
    if (!esCliente && !esOperador) return;

    // Se pide de entrada y se espera al final: es la mitad del contenido del
    // panel y no tiene por qué sumar su latencia a la de la llave.
    const pendientes = this.leerPendientes();

    const faltaUsuario = esCliente ? await this.leFaltaElUsuario() : false;
    const novedades = await pendientes;

    if (!faltaUsuario && !novedades.total) return;

    const rutaBandeja = esOperador
      ? '/wallet/admin/messages-inbox'
      : '/wallet/client/messages-inbox';

    const r = await this.alerts.fire(
      this.armarOpciones(faltaUsuario, novedades),
      'oferta',
    );

    // La marca se pone sólo si el panel llegó a verse. Si el portero lo
    // descartó porque había otra cosa en pantalla, se vuelve a ofrecer.
    if (this.fueMostrado(r)) {
      sessionStorage.setItem(YA_OFRECIDO, 'true');
    }

    if (r.isConfirmed) {
      this.router.navigate([faltaUsuario ? '/wallet/client/referidos' : rutaBandeja]);
      return;
    }

    if (r.isDenied) {
      this.router.navigate([rutaBandeja]);
    }
  }

  /** Los grupos del token. Vacío si no se pueden leer. */
  private async gruposDeCognito(): Promise<string[]> {
    try {
      const session: any = await this.cognito.getSession();
      return session?.accessToken?.payload?.['cognito:groups'] ?? [];
    } catch {
      return [];
    }
  }

  /**
   * `true` si es cliente, no tiene llave y hay una campaña activa de su país.
   *
   * Sin campaña de su país no se le ofrece nada: un venezolano no ve la
   * invitación de una campaña de Chile, ni al revés (`miPrograma` lo decide
   * con su documento). Si algo falla tampoco se ofrece: sin respuesta del
   * servidor no se molesta a nadie. El panel puede abrirse igual por las
   * novedades.
   */
  private async leFaltaElUsuario(): Promise<boolean> {
    try {
      const programa = await this.llaveService.miPrograma();
      if (!programa.visible) return false;

      const { llave } = await this.llaveService.miLlave();
      if (llave) return false;

      // Las condiciones son las de la campaña de su país.
      const c = programa.condiciones;
      const bono = this.llaveService.bonoReferidorTexto(c);
      const gatillo = this.llaveService.gatilloTexto(c);
      this.bono = bono && gatillo ? bono : '';
      this.gatillo = gatillo;
      this.alcance = this.llaveService.alcanceReferidorTexto(c);

      return true;
    } catch {
      return false;
    }
  }

  /** Monto del bono como texto, o vacío si el programa está apagado. */
  private bono = '';
  /** Qué tiene que hacer el invitado para que quien invita cobre. */
  private gatillo = '';
  /** «Aplica para quienes se registren con documento de Chile.», o vacío. */
  private alcance = '';

  /**
   * Los mensajes sin leer. Es la misma consulta que hacía el navbar.
   *
   * No lanza: si el buzón no responde, el panel se abre igual con lo que sí
   * haya que hacer.
   */
  private async leerPendientes(): Promise<Pendientes> {
    try {
      const pendientes = await this.mensajes.getPending();
      const lista = Array.isArray(pendientes) ? pendientes : [];

      return {
        total: lista.length,
        items: lista.slice(0, NOVEDADES_VISIBLES).map((m: any) => ({
          asunto: this.escapar(String(m?.subject ?? 'Mensaje')),
          cuando: this.hace(m?.createdAt),
        })),
      };
    } catch {
      return { items: [], total: 0 };
    }
  }

  /**
   * Un popup descartado por el portero devuelve `dismiss: 'close'`, que es un
   * motivo que el usuario no puede producir: cerrar a mano da `cancel`, `esc`
   * o `backdrop`. Eso distingue "no lo quiso" de "no lo vio".
   */
  private fueMostrado(r: SweetAlertResult): boolean {
    return r.isConfirmed || r.isDenied || r.dismiss !== Swal.DismissReason.close;
  }

  /**
   * Las opciones del panel según lo que haya pendiente.
   *
   * Un solo botón principal, siempre: el camino más importante. El segundo
   * aparece sólo cuando hay dos cosas distintas que atender, y nunca hay tres
   * caminos compitiendo por la atención.
   */
  private armarOpciones(faltaUsuario: boolean, novedades: Pendientes): SweetAlertOptions {
    const soloNovedades = !faltaUsuario;

    return {
      title: soloNovedades
        ? `Tienes ${novedades.total} ${novedades.total === 1 ? 'mensaje' : 'mensajes'} sin leer`
        : 'Antes de empezar',
      html: this.armarCuerpo(faltaUsuario, novedades),
      icon: 'info',
      width: '30rem',
      showCancelButton: true,
      showDenyButton: faltaUsuario && novedades.total > 0,
      confirmButtonText: soloNovedades ? 'Ver bandeja' : 'Crear mi usuario',
      denyButtonText: 'Ver mis avisos',
      cancelButtonText: 'Ahora no',
      confirmButtonColor: '#2F4A2B',
      denyButtonColor: '#8a8f98',
      reverseButtons: true,
      customClass: { actions: 'ag-bv-acciones' },
    };
  }

  /**
   * El cuerpo del panel.
   *
   * Los estilos van embebidos porque el popup vive fuera del componente y no
   * lo alcanza su hoja de estilos. Todo en `rem` y con `overflow-wrap`: un
   * asunto largo tiene que cortarse, no empujar el ancho. Abajo de 480 px los
   * botones pasan a ocupar el ancho completo, uno por línea, y la fecha se va
   * a su propio renglón en vez de apretar el asunto.
   */
  private armarCuerpo(faltaUsuario: boolean, novedades: Pendientes): string {
    const estilos = `
      <style>
        .ag-bv { text-align: left; font-size: .92rem; line-height: 1.45; }
        .ag-bv-seccion { border-top: 1px solid #e5e7eb; margin-top: .9rem; padding-top: .75rem; }
        .ag-bv-seccion:first-child { border-top: 0; margin-top: 0; padding-top: 0; }
        .ag-bv-label { margin: 0 0 .35rem; font-size: .72rem; text-transform: uppercase; letter-spacing: .5px; color: #8a8f98; }
        .ag-bv-texto { margin: 0; color: #082c22; overflow-wrap: anywhere; }
        .ag-bv-lista { list-style: none; margin: .25rem 0 0; padding: 0; max-height: 9rem; overflow-y: auto; }
        .ag-bv-item { display: flex; flex-wrap: wrap; gap: .25rem .5rem; align-items: baseline; padding: .35rem 0; border-bottom: 1px solid #f3f4f6; }
        .ag-bv-item:last-child { border-bottom: 0; }
        .ag-bv-punto { flex: 0 0 auto; color: #0e7705; }
        .ag-bv-asunto { flex: 1 1 60%; min-width: 0; overflow-wrap: anywhere; color: #082c22; }
        .ag-bv-fecha { flex: 0 0 auto; font-size: .7rem; color: #8a8f98; }
        .ag-bv-mas { margin: .5rem 0 0; font-size: .78rem; color: #8a8f98; }
        .ag-bv-acciones { flex-wrap: wrap !important; }
        @media (max-width: 480px) {
          .ag-bv-acciones { flex-direction: column !important; }
          .ag-bv-acciones .swal2-styled { width: 100% !important; margin: .25rem 0 !important; }
        }
      </style>
    `;

    const secciones: string[] = [];

    if (faltaUsuario) {
      const promesa = this.bono
        ? `Es tu identidad para invitar. Con &eacute;l ganas <b>${this.bono}</b> por cada persona que abra su cuenta y ${this.gatillo}.${this.alcance ? ' ' + this.alcance : ''}`
        : 'Ser&aacute; tu identidad en Aguacate y la necesitar&aacute;s para participar en nuestras pr&oacute;ximas promociones de referidos.';

      secciones.push(`
        <div class="ag-bv-seccion">
          <p class="ag-bv-label">Te falta</p>
          <p class="ag-bv-texto">Todav&iacute;a no tienes tu usuario de Aguacate. ${promesa}</p>
        </div>
      `);
    }

    if (novedades.total) {
      const items = novedades.items
        .map(
          (n) => `
            <li class="ag-bv-item">
              <span class="ag-bv-punto">&bull;</span>
              <span class="ag-bv-asunto">${n.asunto}</span>
              <span class="ag-bv-fecha">${n.cuando}</span>
            </li>
          `,
        )
        .join('');

      const restantes = novedades.total - novedades.items.length;
      const mas =
        restantes > 0
          ? `<p class="ag-bv-mas">y ${restantes} ${restantes === 1 ? 'aviso m&aacute;s' : 'avisos m&aacute;s'}</p>`
          : '';

      secciones.push(`
        <div class="ag-bv-seccion">
          <p class="ag-bv-label">Novedades (${novedades.total})</p>
          <ul class="ag-bv-lista">${items}</ul>
          ${mas}
        </div>
      `);
    }

    return `${estilos}<div class="ag-bv">${secciones.join('')}</div>`;
  }

  /** Antigüedad en texto corto. Una fecha ilegible se omite. */
  private hace(iso: unknown): string {
    const ms = Date.parse(String(iso ?? ''));
    if (!Number.isFinite(ms)) return '';

    const minutos = Math.max(0, Math.floor((Date.now() - ms) / 60000));
    if (minutos < 1) return 'ahora';
    if (minutos < 60) return `hace ${minutos} min`;

    const horas = Math.floor(minutos / 60);
    if (horas < 24) return `hace ${horas} h`;

    const dias = Math.floor(horas / 24);
    return dias === 1 ? 'ayer' : `hace ${dias} d`;
  }

  /** El asunto lo escribe un operador: no va crudo al DOM del popup. */
  private escapar(texto: string): string {
    return texto
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}
